import { useState } from "react";
import { SubmissionError, submit } from "redux-form";

import { Box, Button, Typography } from "@braintrust/braintrust-ui-components";
import { US_COUNTRY_CODE } from "@js/apps/common/constants";
import { useUser } from "@js/apps/common/hooks/use-user";
import { Modal } from "@js/components/modal";
import { Snackbar } from "@js/components/snackbar";
import { useAppDispatch } from "@js/hooks";
import type { StripeACH } from "@js/types/payments";

import { StripeACHBankAccountTypes } from "../../constants";

import type { ACHMethodFormData } from "./form";
import { ACH_FORM_ID, ACHForm } from "./form";

import style from "./style.module.scss";

export type AddStripeACHOnSubmitValues = ACHMethodFormData & {
  currency: string;
  usage: string;
};

type AddStripeACHProps = {
  onSubmit: (
    values: AddStripeACHOnSubmitValues,
  ) => Promise<StripeACH | undefined>;
};

const ADD_STRIPE_ACH_MODAL_ID = "ADD_STRIPE_ACH_MODAL";
const POST_ADD_STRIPE_ACH_MODAL_ID = "POST_STRIPE_ACH_MODAL_ID";

export const AddStripeACHModalInstance = Modal(ADD_STRIPE_ACH_MODAL_ID, {
  keepOnBackdropClick: true,
});
const PostAddStripeACHModalInstance = Modal(POST_ADD_STRIPE_ACH_MODAL_ID, {
  className: style.modalContent,
  keepOnBackdropClick: true,
  closeButtonProps: { sx: { svg: { m: 0 } } },
});

export const AddStripeACH = ({ onSubmit }: AddStripeACHProps) => {
  const dispatch = useAppDispatch();
  const [submitting, setSubmitting] = useState(false);

  const user = useUser();

  const handleOnSubmit = async (values: ACHMethodFormData) => {
    try {
      const updatedValues = {
        ...values,
        currency: SETTINGS.DEFAULT_CURRENCY.toLowerCase(),
        usage: "source", // "You can validate a bank account at creation time by passing the parameter usage='source'."
      };

      setSubmitting(true);

      const apiSubmitResult = await onSubmit({
        ...updatedValues,
      });

      if (apiSubmitResult === undefined) {
        throw new Error("Something went wrong.");
      }

      PostAddStripeACHModalInstance.open();
      AddStripeACHModalInstance.close();

      return apiSubmitResult;
    } catch (error) {
      const _error = error as {
        data?: Record<string, string>;
        message?: string;
      };

      Snackbar.error(
        _error.data?._error || "Failed to add payment method, please try again",
        { preventDuplicate: true },
      );

      throw new SubmissionError(_error.data || { _error: _error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const validate = (values: ACHMethodFormData) => {
    const errors: { [k in keyof ACHMethodFormData]?: string } = {};

    if (!values["country"]) {
      errors["country"] = "This field is required.";
    }

    if (!values["account_holder_type"]) {
      errors["account_holder_type"] = "This field is required.";
    }

    if (!values["account_holder_name"]) {
      errors["account_holder_name"] = "This field is required.";
    }

    if (!values["routing_number"]) {
      errors["routing_number"] = "This field is required.";
    }

    if (!values["account_number"]) {
      errors["account_number"] = "This field is required.";
    }

    return errors;
  };

  return (
    <div>
      <AddStripeACHModalInstance>
        <div className={style.modalContent}>
          <Typography component="h1" variant="title" fontWeight={400}>
            Add ACH Details
          </Typography>
          <div className={style.formElement}>
            <ACHForm
              onSubmit={handleOnSubmit}
              validate={validate}
              initialValues={{
                country: US_COUNTRY_CODE,
                account_holder_type: StripeACHBankAccountTypes.COMPANY,
                account_holder_name: user?.public_name,
              }}
            />
          </div>
          <div className="buttons right">
            <Button variant="primary" onClick={AddStripeACHModalInstance.close}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={submitting}
              onClick={() => dispatch(submit(ACH_FORM_ID))}
            >
              Save
            </Button>
          </div>
        </div>
      </AddStripeACHModalInstance>
      <PostAddStripeACHModalInstance>
        <Typography
          component="h2"
          variant="paragraph"
          size="large"
          sx={{ mb: 2 }}
          fontWeight={500}
        >
          Verify Payment Method
        </Typography>
        <Typography component="p" sx={{ mb: 4 }}>
          The payment method has to be verified before you can transfer the
          refundable deposit. To verify this payment method, you will need to
          enter the six-character code (starting with <strong>SM</strong>) found
          in the microdeposit sent to your bank account.
          <br />
          <br />
          The microdeposit should appear in the selected bank account within 1–2
          days. If you are sending an offer to talent and using ACH, the offer
          will be sent once the total offer deposit has been collected. For
          immediate verification, enter a credit card instead.
        </Typography>
        <Box sx={{ textAlign: "end" }}>
          <Button
            variant="secondary"
            color="secondary"
            onClick={PostAddStripeACHModalInstance.close}
          >
            Close
          </Button>
        </Box>
      </PostAddStripeACHModalInstance>
    </div>
  );
};
